import React, { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Drawer from "@mui/material/Drawer";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Box,
  Button,
  Grid,
  Typography,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { axiosInstance } from "../../utils/axios";

// Helper function to parse event dates which can come in different formats
const parseEventDate = (dateValue) => {
  if (!dateValue) return null;

  // If it's already a Date object
  if (dateValue instanceof Date) return dateValue;

  // If it's a numeric string (timestamp in milliseconds)
  if (typeof dateValue === "string" && /^\d+$/.test(dateValue)) {
    return new Date(parseInt(dateValue, 10));
  }

  // If it's a number
  if (typeof dateValue === "number") {
    return new Date(dateValue);
  }

  // If it's a date-only string (YYYY-MM-DD), parse it without timezone conversion
  if (typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    const [year, month, day] = dateValue.split("-").map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }

  // Otherwise try to parse as ISO string or other date format
  const parsed = new Date(dateValue);

  // Check if the date is valid
  if (isNaN(parsed.getTime())) {
    console.error("Invalid date value:", dateValue);
    return null;
  }

  return parsed;
};

const createValidationSchema = (eventData) =>
  Yup.object({
    name: Yup.string().required("Session name is required"),
    sessionDate: Yup.date()
      .required("Session date is required")
      .test("within-event-bounds", function (value) {
        console.log(
          "🔍 Session date validation triggered with:",
          value,
          "eventData:",
          eventData
        );
        if (!eventData || !value) {
          console.log("⚠️ Validation skipped - missing eventData or value", {
            eventData: !!eventData,
            value: !!value,
          });
          return true;
        }

        console.log("🔍 Raw event dates from backend:", {
          startDate: eventData.startDate,
          endDate: eventData.endDate,
          startDateType: typeof eventData.startDate,
          endDateType: typeof eventData.endDate,
        });

        const eventStartDate = parseEventDate(eventData.startDate);
        const eventEndDate = parseEventDate(eventData.endDate);

        console.log("🔍 Parsed event dates:", {
          eventStartDate: eventStartDate?.toISOString(),
          eventEndDate: eventEndDate?.toISOString(),
        });

        if (!eventStartDate || !eventEndDate) {
          console.error(
            "❌ Failed to parse event dates:",
            eventData.startDate,
            eventData.endDate
          );
          return true; // Skip validation if dates are invalid
        }

        // Set time to start of day for date comparison
        eventStartDate.setHours(0, 0, 0, 0);
        eventEndDate.setHours(23, 59, 59, 999);

        const sessionDate = new Date(value);
        sessionDate.setHours(0, 0, 0, 0);

        console.log("🔍 Date comparison:");
        console.log(
          "  Session date:",
          sessionDate.toISOString(),
          sessionDate.getTime()
        );
        console.log(
          "  Event start date:",
          eventStartDate.toISOString(),
          eventStartDate.getTime()
        );
        console.log(
          "  Event end date:",
          eventEndDate.toISOString(),
          eventEndDate.getTime()
        );
        console.log("  Session < eventStart:", sessionDate < eventStartDate);
        console.log("  Session > eventEnd:", sessionDate > eventEndDate);

        if (sessionDate < eventStartDate) {
          const errorMsg = `Session date must be on or after event start date (${eventStartDate.toLocaleDateString()})`;
          console.error("❌ Validation failed:", errorMsg);
          return this.createError({
            message: errorMsg,
          });
        }

        if (sessionDate > eventEndDate) {
          const errorMsg = `Session date must be on or before event end date (${eventEndDate.toLocaleDateString()})`;
          console.error("❌ Validation failed:", errorMsg);
          return this.createError({
            message: errorMsg,
          });
        }

        console.log("✅ Date validation passed");

        return true;
      }),
    sessionTime: Yup.date()
      .required("Session time is required")
      .test("within-event-time-bounds", function (value) {
        if (!eventData || !value || !this.parent.sessionDate) return true;

        console.log("⏰ Time validation triggered");
        console.log("  Session time value:", value);
        console.log("  Session date:", this.parent.sessionDate);

        const sessionDate = new Date(this.parent.sessionDate);
        const sessionTime = new Date(value);

        // Parse event start and end times (these represent daily operating hours)
        let eventStartTime, eventEndTime;

        // Handle both datetime string and time string formats for start time
        if (eventData.startTime.includes("T")) {
          // Full datetime string like "2025-08-06T07:30:00.000Z"
          eventStartTime = new Date(eventData.startTime);
        } else {
          // Simple time string like "07:30:00"
          const [hours, minutes, seconds = 0] = eventData.startTime
            .split(":")
            .map(Number);
          eventStartTime = new Date();
          eventStartTime.setHours(hours, minutes, seconds, 0);
        }

        // Handle both datetime string and time string formats for end time
        if (eventData.endTime.includes("T")) {
          // Full datetime string like "2025-08-06T11:30:00.000Z"
          eventEndTime = new Date(eventData.endTime);
        } else {
          // Simple time string like "11:30:00"
          const [hours, minutes, seconds = 0] = eventData.endTime
            .split(":")
            .map(Number);
          eventEndTime = new Date();
          eventEndTime.setHours(hours, minutes, seconds, 0);
        }

        // Create DateTime objects for comparison using the session date with event daily times
        const sessionDateTime = new Date(sessionDate);
        sessionDateTime.setHours(
          sessionTime.getHours(),
          sessionTime.getMinutes(),
          sessionTime.getSeconds()
        );

        const dailyStartDateTime = new Date(sessionDate);
        dailyStartDateTime.setHours(
          eventStartTime.getHours(),
          eventStartTime.getMinutes(),
          0
        );

        const dailyEndDateTime = new Date(sessionDate);
        dailyEndDateTime.setHours(
          eventEndTime.getHours(),
          eventEndTime.getMinutes(),
          0
        );

        // Check if session time is within daily operating hours
        console.log("⏰ Time comparison:");
        console.log("  Session DateTime:", sessionDateTime.toISOString());
        console.log(
          "  Daily Start DateTime:",
          dailyStartDateTime.toISOString()
        );
        console.log("  Daily End DateTime:", dailyEndDateTime.toISOString());

        // Helper function to format time in 12-hour format
        const formatTime = (date) => {
          let hours = date.getHours();
          const minutes = date.getMinutes();
          const ampm = hours >= 12 ? "PM" : "AM";
          hours = hours % 12;
          hours = hours ? hours : 12; // 0 should be 12
          const minutesStr = minutes < 10 ? "0" + minutes : minutes;
          return `${hours}:${minutesStr} ${ampm}`;
        };

        if (sessionDateTime < dailyStartDateTime) {
          const errorMsg = `Session time must be after daily start time (${formatTime(
            dailyStartDateTime
          )})`;
          console.error("❌ Time validation failed:", errorMsg);
          return this.createError({
            message: errorMsg,
          });
        }

        if (sessionDateTime > dailyEndDateTime) {
          const errorMsg = `Session time must be before daily end time (${formatTime(
            dailyEndDateTime
          )})`;
          console.error("❌ Time validation failed:", errorMsg);
          return this.createError({
            message: errorMsg,
          });
        }

        console.log("✅ Time validation passed");
        return true;
      }),
    duration: Yup.number()
      .required("Duration is required")
      .positive("Duration must be a positive number")
      .test("session-end-within-daily-hours", function (value) {
        if (
          !eventData ||
          !value ||
          !this.parent.sessionDate ||
          !this.parent.sessionTime
        )
          return true;

        const sessionDate = new Date(this.parent.sessionDate);
        const sessionTime = new Date(this.parent.sessionTime);

        // Calculate session end time
        const sessionStart = new Date(sessionDate);
        sessionStart.setHours(
          sessionTime.getHours(),
          sessionTime.getMinutes(),
          sessionTime.getSeconds()
        );

        const sessionEnd = new Date(sessionStart);
        sessionEnd.setMinutes(sessionStart.getMinutes() + parseInt(value));

        // Parse event end time (daily operating hours end)
        let eventEndTime;

        console.log("Event End Time begin &&&&&&&&: ", eventEndTime, eventData);
        if (eventData.endTime.includes("T")) {
          // Full datetime string like "2025-08-06T11:30:00.000Z"
          eventEndTime = new Date(eventData.endTime);
          console.log("Event End Time final &&&&&&&&: ", eventEndTime);
        } else {
          console.log(
            "Event End Time final else begining &&&&&&&&: ",
            eventEndTime,
            eventData.endTime
          );
          // Simple time string like "11:30:00"
          const [hours, minutes, seconds = 0] = eventData.endTime
            .split(":")
            .map(Number);
          eventEndTime = new Date();
          eventEndTime.setHours(hours, minutes, seconds, 0);
          console.log(
            "Event End Time final else ending &&&&&&&&: ",
            eventEndTime
          );
        }

        // Create daily end time for the session date
        const dailyEndDateTime = new Date(sessionDate);
        dailyEndDateTime.setHours(
          eventEndTime.getHours(),
          eventEndTime.getMinutes(),
          0
        );

        console.log("sessionDate &&&&&&&&: ", sessionDate);
        console.log("dailyEndDateTime &&&&&&&&: ", dailyEndDateTime);
        console.log("sessionEnd &&&&&&&&: ", sessionEnd);

        // Check if session end time exceeds daily operating hours
        if (sessionEnd > dailyEndDateTime) {
          // Format times for display in 12-hour format
          const formatTime = (date) => {
            let hours = date.getHours();
            const minutes = date.getMinutes();
            const ampm = hours >= 12 ? "PM" : "AM";
            hours = hours % 12;
            hours = hours ? hours : 12; // 0 should be 12
            const minutesStr = minutes < 10 ? "0" + minutes : minutes;
            return `${hours}:${minutesStr} ${ampm}`;
          };

          return this.createError({
            message: `Session end time (${formatTime(
              sessionEnd
            )}) must be before daily end time (${formatTime(
              dailyEndDateTime
            )})`,
          });
        }

        // Also check if session spans to next day (crosses midnight)
        if (sessionEnd.getDate() !== sessionStart.getDate()) {
          return this.createError({
            message: `Session cannot span across multiple days. Please reduce the duration.`,
          });
        }

        return true;
      }),
    description: Yup.string().required("Description is required"),
  });

function AvailableTracks({ eventId, onTrackSelect, selectedTracks = [] }) {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!eventId) return;

    const fetchTracks = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.post("/graphql", {
          query: `
            query Tracks($eventId: ID!) {
              tracks(eventId: $eventId) {
                id
                name
                description
              }
            }
          `,
          variables: {
            eventId,
          },
        });

        if (response.errors) {
          throw new Error(
            response.errors[0]?.message || "Failed to fetch tracks"
          );
        }

        const fetchedTracks = response.data?.tracks || [];
        setTracks(fetchedTracks);
      } catch (error) {
        console.error("Error fetching tracks:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTracks();
  }, [eventId]);

  return (
    <Grid item xs={12}>
      <Autocomplete
        multiple
        options={tracks}
        value={selectedTracks || []}
        getOptionLabel={(option) => option.name}
        onChange={(event, newValue) => onTrackSelect(newValue)}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tracks"
            placeholder="Select Tracks"
            fullWidth
            helperText="Assign this session to specific tracks"
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              key={option.id}
              label={option.name}
              {...getTagProps({ index })}
            />
          ))
        }
      />
    </Grid>
  );
}

function SessionTags({ onTagChange, selectedTags = [] }) {
  return (
    <Grid item xs={12}>
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={selectedTags || []}
        onChange={(event, newValue) => onTagChange(newValue)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip key={index} label={option} {...getTagProps({ index })} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tags"
            placeholder="Add Tags (Press Enter after each tag)"
            fullWidth
            helperText="Add tags to help categorize your session"
          />
        )}
      />
    </Grid>
  );
}

function AttendeeSelector({
  eventId,
  category,
  label,
  value,
  onChange,
  multiple = false,
  required = false,
}) {
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!eventId || !category) return;

    const fetchAttendees = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.post("/graphql", {
          query: `
            query GetEventAttendees($eventId: ID!, $category: String) {
              getEventAttendees(eventId: $eventId, category: $category) {
                id
                firstName
                lastName
                email
                jobTitle
                organization
                category
              }
            }
          `,
          variables: {
            eventId,
            category,
          },
        });

        if (response.data.errors) {
          throw new Error(
            response.data.errors[0]?.message || `Failed to fetch ${category}s`
          );
        }

        const fetchedAttendees = response.data?.getEventAttendees || [];
        setAttendees(fetchedAttendees);
      } catch (error) {
        console.error(`Error fetching ${category}s:`, error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendees();
  }, [eventId, category]);

  const getOptionLabel = (option) => {
    if (!option || (!option.firstName && !option.lastName)) return "";
    return `${option.firstName} ${option.lastName} ${
      option.organization ? `(${option.organization})` : ""
    }`;
  };

  console.log(`🔍 AttendeeSelector (${category}):`, {
    eventId,
    category,
    attendees: attendees.length,
    value: value,
    valueLength: Array.isArray(value) ? value.length : value ? 1 : 0,
    loading,
    error,
  });

  return (
    <Autocomplete
      multiple={multiple}
      options={attendees}
      value={value || (multiple ? [] : null)}
      onChange={(event, newValue) => {
        console.log(`🔄 AttendeeSelector onChange (${category}):`, newValue);
        onChange(newValue);
      }}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      loading={loading}
      renderOption={(props, option) => (
        <li {...props}>
          <div>
            <Typography variant="body1">{`${option.firstName} ${option.lastName}`}</Typography>
            <Typography variant="caption" color="textSecondary">
              {option.jobTitle}{" "}
              {option.organization ? `at ${option.organization}` : ""}
            </Typography>
          </div>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          error={Boolean(error)}
          helperText={error || (loading ? `Loading ${category}s...` : "")}
        />
      )}
    />
  );
}

function Session({
  eventId,
  onSessionAdded,
  editMode = false,
  sessionToEdit = null,
  onClose = null,
}) {
  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [eventData, setEventData] = useState(null);

  // Fetch event data for validation
  useEffect(() => {
    if (eventId) {
      const fetchEventData = async () => {
        try {
          const response = await axiosInstance.post("/graphql", {
            query: `
              query GetEvent($id: ID!) {
                event(id: $id) {
                  id
                  name
                  startDate
                  endDate
                  startTime
                  endTime
                }
              }
            `,
            variables: { id: eventId },
          });

          console.log("🔍 Full GraphQL response:", response);
          console.log("🔍 Response.data:", response.data);
          console.log("🔍 Response.data.data:", response.data?.data);
          console.log(
            "🔍 Response.data.data.event:",
            response.data?.data?.event
          );

          // Handle different response structures
          const eventData = response.data?.data?.event || response.data?.event;

          if (eventData) {
            console.log("✅ Event data found in response");
            console.log("🎯 Event data received for validation:", eventData);
            console.log(
              "🎯 Event startDate:",
              eventData.startDate,
              "type:",
              typeof eventData.startDate,
              "parsed:",
              parseEventDate(eventData.startDate)
            );
            console.log(
              "🎯 Event endDate:",
              eventData.endDate,
              "type:",
              typeof eventData.endDate,
              "parsed:",
              parseEventDate(eventData.endDate)
            );
            console.log(
              "🎯 Event startTime:",
              eventData.startTime,
              "type:",
              typeof eventData.startTime
            );
            console.log(
              "🎯 Event endTime:",
              eventData.endTime,
              "type:",
              typeof eventData.endTime
            );
            setEventData(eventData);

            // Trigger validation once event data is loaded
            console.log("🔄 Triggering form validation after event data load");
          } else {
            console.error("❌ No event data found in response:", response.data);
          }
        } catch (error) {
          console.error("❌ Error fetching event data:", error);
          console.error("❌ Error details:", error.response?.data);
        }
      };

      fetchEventData();
    }
  }, [eventId]);

  const toggleDrawer = (isOpen) => {
    if (editMode && onClose) {
      // In edit mode, use the parent's close handler
      if (!isOpen) {
        formik.resetForm();
        onClose();
      }
    } else {
      // In add mode, use internal state
      setOpen(isOpen);
      if (!isOpen) {
        formik.resetForm();
      }
    }
  };

  // Since sessionToEdit now includes full attendee objects, we don't need to fetch them separately
  const attendeeObjects = useMemo(() => {
    if (editMode && sessionToEdit) {
      console.log("🔍 Using attendees from sessionToEdit");
      console.log("sessionToEdit.hosts:", sessionToEdit.hosts);
      console.log("sessionToEdit.coHosts:", sessionToEdit.coHosts);
      console.log("sessionToEdit.speakers:", sessionToEdit.speakers);

      return {
        hosts: sessionToEdit.hosts || [],
        coHosts: sessionToEdit.coHosts || [],
        speakers: sessionToEdit.speakers || [],
      };
    }
    return { hosts: [], coHosts: [], speakers: [] };
  }, [editMode, sessionToEdit]);

  // Memoized initial values that properly depend on all required data
  const initialValues = useMemo(() => {
    console.log("🔄 Computing initial values...");
    console.log("  editMode:", editMode);
    console.log("  sessionToEdit:", sessionToEdit);
    console.log("  attendeeObjects:", attendeeObjects);
    console.log("  attendeeObjects.hosts:", attendeeObjects.hosts);
    console.log("  attendeeObjects.coHosts:", attendeeObjects.coHosts);
    console.log("  attendeeObjects.speakers:", attendeeObjects.speakers);

    if (editMode && sessionToEdit) {
      // Parse the sessionDate from timestamp string to Date object
      let sessionDate = null;
      if (sessionToEdit.sessionDate) {
        try {
          if (
            typeof sessionToEdit.sessionDate === "string" &&
            /^\d+$/.test(sessionToEdit.sessionDate)
          ) {
            sessionDate = new Date(parseInt(sessionToEdit.sessionDate, 10));
          } else {
            sessionDate = new Date(sessionToEdit.sessionDate);
          }
          // Check if date is valid
          if (isNaN(sessionDate.getTime())) {
            console.error("Invalid sessionDate:", sessionToEdit.sessionDate);
            sessionDate = null;
          }
        } catch (error) {
          console.error("Error parsing sessionDate:", error);
          sessionDate = null;
        }
      }

      // Parse sessionTime to Date object for TimePicker
      let sessionTime = null;
      if (sessionToEdit.sessionTime) {
        try {
          // Create a date with the time from sessionTime
          const today = new Date();
          const timeParts = sessionToEdit.sessionTime.split(":");
          const hours = parseInt(timeParts[0] || 0, 10);
          const minutes = parseInt(timeParts[1] || 0, 10);
          const seconds = parseInt(timeParts[2] || 0, 10);

          sessionTime = new Date(today);
          sessionTime.setHours(hours, minutes, seconds);
        } catch (error) {
          console.error("Error parsing sessionTime:", error);
          sessionTime = null;
        }
      }

      const values = {
        name: sessionToEdit.name || "",
        sessionDate: sessionDate,
        sessionTime: sessionTime,
        duration: sessionToEdit.duration?.toString() || "",
        description: sessionToEdit.description || "",
        hosts: attendeeObjects.hosts || [],
        coHosts: attendeeObjects.coHosts || [],
        speakers: attendeeObjects.speakers || [],
        tracks: sessionToEdit.tracks || [],
        tags: sessionToEdit.tags || [],
      };

      console.log("✅ Edit mode initial values with attendees:", values);
      console.log("  hosts count:", values.hosts.length);
      console.log("  coHosts count:", values.coHosts.length);
      console.log("  speakers count:", values.speakers.length);
      return values;
    }

    const defaultValues = {
      name: "",
      sessionDate: null,
      sessionTime: null,
      duration: "",
      description: "",
      hosts: [],
      coHosts: [],
      speakers: [],
      tracks: [],
      tags: [],
    };

    console.log("✅ Default initial values:", defaultValues);
    return defaultValues;
  }, [editMode, sessionToEdit, attendeeObjects]);

  // Create validation schema that updates when eventData changes
  const validationSchema = useMemo(() => {
    console.log("🔄 Creating validation schema with eventData:", eventData);
    if (!eventData) {
      console.log("⚠️ No eventData available, using basic schema");
      return Yup.object({
        name: Yup.string().required("Session name is required"),
        sessionDate: Yup.date().required("Session date is required"),
        sessionTime: Yup.date().required("Session time is required"),
        duration: Yup.number()
          .required("Duration is required")
          .min(1, "Duration must be at least 1 minute"),
      });
    }
    return createValidationSchema(eventData);
  }, [eventData]);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      try {
        setSubmitting(true);

        console.log("📝 Form submission started");
        console.log("📝 Form submission values:", values);
        console.log("📝 Session date (local):", values.sessionDate);
        console.log("📝 Session time (local):", values.sessionTime);
        console.log("📝 Form validation state:", {
          isValid: formik.isValid,
          errors: formik.errors,
          touched: formik.touched,
        });

        // Check if event data is loaded before allowing submission
        if (!eventData) {
          console.error("❌ Event data not loaded, preventing submission");
          setSubmitting(false);
          return;
        }

        // Double-check validation before submission
        if (!formik.isValid) {
          console.error("❌ Form is invalid, preventing submission");
          setSubmitting(false);
          return;
        }

        // Convert date and time to expected format
        const sessionDate = values.sessionDate
          ? values.sessionDate.toISOString().split("T")[0]
          : null;

        const sessionTime = values.sessionTime
          ? values.sessionTime.toISOString().split("T")[1].split(".")[0] // HH:MM:SS in UTC
          : null;

        console.log("📝 Session date (ISO for storage):", sessionDate);
        console.log("📝 Session time (UTC for storage):", sessionTime);

        // Extract IDs for hosts, co-hosts, speakers and tracks
        const hostIds = Array.isArray(values.hosts)
          ? values.hosts.map((h) => h.id)
          : values.hosts?.id
          ? [values.hosts.id]
          : [];

        const coHostIds = Array.isArray(values.coHosts)
          ? values.coHosts.map((h) => h.id)
          : [];

        const speakerIds = Array.isArray(values.speakers)
          ? values.speakers.map((s) => s.id)
          : [];

        const trackIds = Array.isArray(values.tracks)
          ? values.tracks.map((t) => t.id)
          : [];

        const isEditMode = editMode && sessionToEdit;
        const mutation = isEditMode
          ? `mutation UpdateSession($id: ID!, $eventId: ID!, $input: UpdateSessionInput!) {
            updateSession(id: $id, eventId: $eventId, input: $input) {
              id
              name
              sessionDate
              sessionTime
              duration
              description
            }
          }`
          : `mutation CreateSession($input: CreateSessionInput!) {
            createSession(input: $input) {
              id
              name
              sessionDate
              sessionTime
              duration
              description
            }
          }`;

        const variables = isEditMode
          ? {
              id: sessionToEdit.id,
              eventId,
              input: {
                name: values.name,
                sessionDate,
                sessionTime,
                duration: parseInt(values.duration, 10),
                hostIds,
                coHostIds,
                speakerIds,
                trackIds,
                tags: values.tags,
              },
            }
          : {
              input: {
                eventId,
                name: values.name,
                sessionDate,
                sessionTime,
                duration: parseInt(values.duration, 10),
                description: values.description,
                hostIds,
                coHostIds,
                speakerIds,
                trackIds,
                tags: values.tags,
              },
            };

        const response = await axiosInstance.post("/graphql", {
          query: mutation,
          variables: variables,
        });

        if (response.errors) {
          throw new Error(
            response.errors[0]?.message || "Failed to create session"
          );
        }

        setAlert({
          open: true,
          message: isEditMode
            ? "Session updated successfully!"
            : "Session created successfully!",
          severity: "success",
        });

        resetForm();
        toggleDrawer(false);

        // Notify parent component to refresh data
        if (typeof onSessionAdded === "function") {
          onSessionAdded();
        }
      } catch (error) {
        console.error("Error creating session:", error);
        setAlert({
          open: true,
          message: error.message || "Failed to create session",
          severity: "error",
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Debug formik state
  useEffect(() => {
    console.log("🔍 Formik state debug:");
    console.log("  editMode:", editMode);
    console.log("  sessionToEdit:", sessionToEdit);
    console.log("  formik.values:", formik.values);
    console.log("  formik.isValid:", formik.isValid);
    console.log("  formik.errors:", formik.errors);
    console.log("  formik.isSubmitting:", formik.isSubmitting);
    console.log("  eventData:", eventData);
    console.log("  attendeeObjects:", attendeeObjects);
  }, [editMode, sessionToEdit, formik.values, formik.isSubmitting]);

  // Force update attendee fields when attendee objects are loaded
  useEffect(() => {
    if (editMode && attendeeObjects && formik && sessionToEdit) {
      console.log("🔄 Updating attendee fields after data load");
      console.log("  attendeeObjects.hosts:", attendeeObjects.hosts);
      console.log("  current formik hosts:", formik.values.hosts);

      // Update fields regardless of current state to ensure proper population
      if (attendeeObjects.hosts?.length > 0) {
        console.log("✅ Setting hosts field");
        formik.setFieldValue("hosts", attendeeObjects.hosts);
      }

      if (attendeeObjects.coHosts?.length > 0) {
        console.log("✅ Setting coHosts field");
        formik.setFieldValue("coHosts", attendeeObjects.coHosts);
      }

      if (attendeeObjects.speakers?.length > 0) {
        console.log("✅ Setting speakers field");
        formik.setFieldValue("speakers", attendeeObjects.speakers);
      }
    }
  }, [attendeeObjects, editMode]);

  // Trigger validation when eventData is loaded
  useEffect(() => {
    if (eventData && formik) {
      console.log("🔄 Event data loaded, triggering form validation");
      // Force validation by touching all fields if they have values
      if (formik.values.sessionDate) {
        formik.setFieldTouched("sessionDate", true);
        formik.validateField("sessionDate");
      }
      if (formik.values.sessionTime) {
        formik.setFieldTouched("sessionTime", true);
        formik.validateField("sessionTime");
      }
      if (formik.values.duration) {
        formik.setFieldTouched("duration", true);
        formik.validateField("duration");
      }
    }
  }, [eventData]);

  return (
    <React.Fragment>
      <Button
        variant="contained"
        color="primary"
        onClick={() => toggleDrawer(true)}
        startIcon={<AddIcon />}
      >
        Session
      </Button>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      <Drawer
        anchor="right"
        open={editMode ? true : open}
        onClose={() => toggleDrawer(false)}
      >
        <Box
          sx={{ width: "600px", padding: "20px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography variant="h3" gutterBottom>
            {editMode ? "Edit Session" : "Add Session"}
          </Typography>

          {/* Event Constraints Display */}

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  id="name"
                  name="name"
                  label="Session Name"
                  fullWidth
                  value={formik.values.name || ""}
                  onChange={(e) => {
                    console.log("📝 Name field changed:", e.target.value);
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={
                    (formik.touched.name && formik.errors.name) ||
                    "Enter a descriptive name for your session"
                  }
                  disabled={formik.isSubmitting}
                />
              </Grid>

              <Grid item xs={4}>
                <DatePicker
                  label="Session Date*"
                  value={formik.values.sessionDate}
                  onChange={(value) =>
                    formik.setFieldValue("sessionDate", value)
                  }
                  minDate={
                    eventData ? parseEventDate(eventData.startDate) : undefined
                  }
                  maxDate={
                    eventData ? parseEventDate(eventData.endDate) : undefined
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error:
                        formik.touched.sessionDate &&
                        Boolean(formik.errors.sessionDate),
                      helperText:
                        (formik.touched.sessionDate &&
                          formik.errors.sessionDate) ||
                        "Select date within event range",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={4}>
                <TimePicker
                  label="Session Time*"
                  value={formik.values.sessionTime}
                  onChange={(value) =>
                    formik.setFieldValue("sessionTime", value)
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error:
                        formik.touched.sessionTime &&
                        Boolean(formik.errors.sessionTime),
                      helperText:
                        (formik.touched.sessionTime &&
                          formik.errors.sessionTime) ||
                        "Select time",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={4}>
                <TextField
                  id="duration"
                  name="duration"
                  label="Duration (Minutes)"
                  type="number"
                  fullWidth
                  value={formik.values.duration || ""}
                  onChange={(e) => {
                    console.log("📝 Duration field changed:", e.target.value);
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.duration && Boolean(formik.errors.duration)
                  }
                  helperText={
                    (formik.touched.duration && formik.errors.duration) ||
                    "Session length in minutes"
                  }
                  InputProps={{ inputProps: { min: 1 } }}
                  disabled={formik.isSubmitting}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  id="description"
                  name="description"
                  label="Description"
                  multiline
                  rows={4}
                  fullWidth
                  value={formik.values.description || ""}
                  onChange={(e) => {
                    console.log(
                      "📝 Description field changed:",
                      e.target.value
                    );
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    (formik.touched.description && formik.errors.description) ||
                    "Provide details about this session"
                  }
                  disabled={formik.isSubmitting}
                />
              </Grid>

              <Grid item xs={12}>
                <AttendeeSelector
                  eventId={eventId}
                  category="Host"
                  label="Host"
                  value={formik.values.hosts}
                  onChange={(newValue) =>
                    formik.setFieldValue("hosts", newValue)
                  }
                  multiple={true}
                  required={false}
                />
              </Grid>

              <Grid item xs={12}>
                <AttendeeSelector
                  eventId={eventId}
                  category="Co Host"
                  label="Co-Hosts"
                  value={formik.values.coHosts}
                  onChange={(newValue) =>
                    formik.setFieldValue("coHosts", newValue)
                  }
                  multiple={true}
                />
              </Grid>

              <Grid item xs={12}>
                <AttendeeSelector
                  eventId={eventId}
                  category="Speaker"
                  label="Speakers"
                  value={formik.values.speakers}
                  onChange={(newValue) =>
                    formik.setFieldValue("speakers", newValue)
                  }
                  multiple={true}
                />
              </Grid>

              <AvailableTracks
                eventId={eventId}
                selectedTracks={formik.values.tracks}
                onTrackSelect={(tracks) =>
                  formik.setFieldValue("tracks", tracks)
                }
              />

              <SessionTags
                selectedTags={formik.values.tags}
                onTagChange={(tags) => formik.setFieldValue("tags", tags)}
              />
            </Grid>

            {/* Validation Summary */}
            {!formik.isValid && Object.keys(formik.errors).length > 0 && (
              <Box
                sx={{ mt: 3, p: 2, bgcolor: "error.light", borderRadius: 1 }}
              >
                <Typography
                  variant="subtitle2"
                  color="error.main"
                  fontWeight="bold"
                  gutterBottom
                >
                  Please fix the following issues:
                </Typography>
                {Object.entries(formik.errors).map(([field, error]) => (
                  <Typography
                    key={field}
                    variant="body2"
                    color="error.main"
                    sx={{ ml: 1 }}
                  >
                    • {error}
                  </Typography>
                ))}
              </Box>
            )}

            <DialogActions sx={{ mt: 4 }}>
              <Button
                onClick={() => toggleDrawer(false)}
                color="inherit"
                disabled={formik.isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={formik.isSubmitting || !formik.isValid || !eventData}
              >
                {formik.isSubmitting ? (
                  <CircularProgress size={24} />
                ) : !eventData ? (
                  "Loading Event Data..."
                ) : editMode ? (
                  `Update Session ${formik.isValid ? "✅" : "❌"}`
                ) : (
                  `Save Session ${formik.isValid ? "✅" : "❌"}`
                )}
              </Button>

              {/* Debug button info */}
              {/* <Box sx={{ mt: 1, fontSize: "12px", color: "text.secondary" }}> */}
              {/*   Form Valid: {String(formik.isValid)} | Has EventData:{" "} */}
              {/*   {String(!!eventData)} | Error Count:{" "} */}
              {/*   {Object.keys(formik.errors).length} */}
              {/* </Box> */}
            </DialogActions>
          </form>
        </Box>
      </Drawer>
    </React.Fragment>
  );
}

export default Session;
